import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BRAND } from "../mock";
import { Lightbox } from "../components/PropertyMedia";
import { MapPin, BedDouble, Bath, Ruler, Play, Clock, Download, ShieldCheck, Link2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const srcOf = (url) => (url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

const remaining = (iso) => {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3600000);
  return h < 24 ? `${h || 1} hour${h === 1 ? "" : "s"}` : `${Math.round(h / 24)} day${Math.round(h / 24) === 1 ? "" : "s"}`;
};

const Hero = ({ item, onOpen }) => {
  if (item.kind === "video" && item.external && item.embed) {
    return <iframe data-testid="share-hero-embed" title={item.name} src={item.embed} allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="aspect-video w-full rounded-2xl border-0 bg-black" />;
  }
  if (item.kind === "video") {
    return <video data-testid="share-hero-video" src={srcOf(item.url)} poster={item.poster ? srcOf(item.poster) : undefined} controls playsInline className="aspect-video w-full rounded-2xl bg-black" />;
  }
  return <button onClick={onOpen} className="block w-full"><img src={srcOf(item.url)} alt="" className="aspect-video w-full rounded-2xl object-cover" /></button>;
};

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/share/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This link isn't available."));
  }, [token]);

  const items = data ? data.items.filter((i) => i.kind !== "file") : [];
  const files = data ? data.items.filter((i) => i.kind === "file") : [];
  const move = useCallback((d) => setLightbox((i) => (i === null ? i : (i + d + items.length) % items.length)), [items.length]);
  useEffect(() => {
    if (lightbox === null) return;
    const h = (e) => { if (e.key === "Escape") setLightbox(null); if (e.key === "ArrowRight") move(1); if (e.key === "ArrowLeft") move(-1); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [lightbox, move]);

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-6 text-center">
        <div data-testid="share-error">
          <img src={BRAND.logo} alt="Moroccan Brothers" className="mx-auto h-16 w-auto object-contain" />
          <h1 className="mt-8 font-display text-2xl font-semibold text-white">{error}</h1>
          <p className="mt-3 text-sm text-muted-foreground">Ask the person who sent it for a fresh link.</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="grid min-h-screen place-items-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-red border-t-transparent" /></div>;

  const [hero, ...rest] = items;
  const place = [data.district, data.city].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-background text-white" data-testid="share-page">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5">
        <img src={BRAND.logo} alt="Moroccan Brothers" className="h-12 w-auto object-contain" />
        <span className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground" data-testid="share-expiry">
          <Clock className="h-3.5 w-3.5 text-brand-green" /> Link expires in {remaining(data.expires_at)}
        </span>
      </header>

      <main className="mx-auto max-w-5xl px-5 pb-20">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-red">Private walkthrough{data.label ? ` · for ${data.label}` : ""}</p>
        <h1 className="mt-2 font-display text-3xl font-bold leading-tight sm:text-4xl" data-testid="share-title">{data.title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {place && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {place}</span>}
          {!!data.beds && <span className="flex items-center gap-1"><BedDouble className="h-4 w-4" /> {data.beds} beds</span>}
          {!!data.baths && <span className="flex items-center gap-1"><Bath className="h-4 w-4" /> {data.baths} baths</span>}
          {!!data.size && <span className="flex items-center gap-1"><Ruler className="h-4 w-4" /> {data.size} m²</span>}
        </div>

        {hero && <div className="mt-8"><Hero item={hero} onOpen={() => setLightbox(0)} /></div>}

        {rest.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="share-grid">
            {rest.map((it, i) => (
              <button key={it.url} onClick={() => setLightbox(i + 1)} data-testid={`share-item-${i}`} className="group relative aspect-[4/3] overflow-hidden rounded-xl bg-black/40">
                {it.kind === "video" ? (
                  it.poster ? <img src={srcOf(it.poster)} alt="" className="h-full w-full object-cover" /> : <video src={`${srcOf(it.url)}#t=0.1`} preload="metadata" muted playsInline className="h-full w-full object-cover" />
                ) : <img src={srcOf(it.url)} alt="" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />}
                {it.kind === "video" && <span className="absolute inset-0 grid place-items-center"><span className="grid h-10 w-10 place-items-center rounded-full bg-black/60"><Play className="ml-0.5 h-4 w-4" fill="currentColor" /></span></span>}
              </button>
            ))}
          </div>
        )}

        {data.summary && <p className="mt-10 max-w-3xl text-base leading-relaxed text-slate-300">{data.summary}</p>}

        {files.length > 0 && (
          <div className="mt-8 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4" data-testid="share-files">
            <p className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">Documents</p>
            {files.map((f, i) => (
              <a key={f.url} href={srcOf(f.url)} target="_blank" rel="noreferrer" data-testid={`share-file-${i}`} className="flex items-center justify-between rounded-lg px-2 py-2 text-sm hover:bg-white/[0.04]">
                <span className="truncate">{f.name}</span><Download className="h-4 w-4 text-muted-foreground" />
              </a>
            ))}
          </div>
        )}

        <footer className="mt-16 flex flex-col gap-3 border-t border-white/[0.06] pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-brand-green" /> Shared privately{data.shared_by ? ` by ${data.shared_by}` : ""} · Moroccan Brothers</span>
          <a href="/" className="flex items-center gap-1.5 hover:text-white"><Link2 className="h-3.5 w-3.5" /> Built on the Moroccan Brothers platform</a>
        </footer>
      </main>

      {lightbox !== null && items[lightbox] && <Lightbox items={items} index={lightbox} onClose={() => setLightbox(null)} onMove={move} srcOf={srcOf} />}
    </div>
  );
}
